import { Box } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { PerformLowerControls } from '../edit-details/perform-view/perform-lower-controls';
import type { FlowSlotDto } from '@/types/dtos/flows/response/flow.dto';
import type { FlowDetailMode } from '../flow-detail.types';
import { useScreenSize } from '@/hooks/use-screen-size';
import { useFlowStore } from '@/state/stores/use-flow-store';
import { getObjectId, resolveLiveBindingFlowId } from '@/helpers/object.id.helper';
import { getEntityRefFromFlow, resolveSocketEntityFromFlow } from '@/helpers/entity-ref.helper';
import { useGetLiveContextStateQuery } from '@/state/queries/player/use-get-live-context-state';
import { applyServerSequenceIndexChange } from '@/helpers/sequence-index-ws.helper';
import { resolveLiveSequenceItemIds } from '@/helpers/resolve-live-sequence-item-ids.helper';
import { GET_LIVE_CONTEXT_STATE_QUERY_KEY } from '@/state/queries/player/use-get-live-context-state';
import { GET_LIVE_LANGUAGE_STATUS_QUERY_KEY } from '@/state/queries/player/use-live-language-status';
import { GET_FLOW_QUERY_KEY } from '@/state/queries/flows/use-get-flow';
import { GET_PLAYERS_QUERY_KEY } from '@/state/queries/players/use-get-players';
import { shouldRefetchQueriesOnLiveContextInvalidated } from '@/helpers/should-refetch-on-live-context-invalidated.helper';
import { FlowControlLiveStateProvider } from '../flow-control-live-state';
import { usePlayerSocket } from '@/hooks/use-player-socket';
import { FlowControlSlotListColumn } from './flow-control-slot-list-column';
import { FlowControlNowNextStack } from './flow-control-now-next-stack';
import type { FlowControlPerformMode } from '@/types/player/simulation-cue-sheet.types';
import type { FlowControlNarrowViewMode } from '@/types/player/flow-control-narrow-view.types';
import { useCallback, useMemo, useEffect, useRef } from 'react';
import { useHydrateFlowControlSimulationCueSheet } from '../slot-list/use-hydrate-flow-control-simulation-cue-sheet';
import { useFlowControlLiveToSimulationReset } from '../slot-list/use-flow-control-live-to-simulation-reset';
import { useFlowControlAutoFollowPlayingSegment } from '../slot-list/use-flow-control-auto-follow-playing-segment';
import { useFlowControlSegmentBrowse } from '../slot-list/use-flow-control-segment-browse';
import { useFlowControlNarrowViewStore } from '@/state/stores/use-flow-control-narrow-view-store';
import { useLiveContextCueState } from '@/hooks/use-live-context-cue-state';
import { useNextCueRecalculating } from '@/hooks/use-next-cue-recalculating';

interface FlowControlTabProps {
  setShowFlowDetails: React.Dispatch<React.SetStateAction<FlowDetailMode | undefined>>;
  setFlowDetailsDirection: React.Dispatch<React.SetStateAction<'up' | 'down' | undefined>>;
  onSlotSelect?: (slot: FlowSlotDto, isUserInitiated?: boolean) => void;
  onShowTimingAlert?: () => void;
  onShowPublishAlert?: () => void;
  selectedSlot?: FlowSlotDto;
  onSlotDeleted?: () => void;
  eventSlotIdParam?: string | null;
  /** Opens the full perform overlay (EditDetailsDialog) on narrow viewports. */
  onRequestPerformOverlay?: (mode?: Exclude<FlowControlNarrowViewMode, 'list'>) => void;
}

const sharedColumnSx = {
  'flex': 1,
  'minWidth': 0,
  'display': 'flex',
  'flexDirection': 'column',
  'overflowY': 'auto',
  'height': '100%',
  'px': 1,
  'pt': 1,
  '&::-webkit-scrollbar': { width: '8px' },
  '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
  '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(0,0,0,0.2)', borderRadius: '4px' },
};

export default function FlowControlTab({
  setShowFlowDetails,
  setFlowDetailsDirection,
  onSlotSelect,
  onShowTimingAlert,
  onShowPublishAlert,
  onRequestPerformOverlay,
  eventSlotIdParam,
}: FlowControlTabProps) {
  const { isWideScreen } = useScreenSize({ breakpoint: 860 });
  const flow = useFlowStore((s) => s.flow);
  const flowPlan = useFlowStore((s) => s.flowPlan);
  const queryClient = useQueryClient();
  const planFlowId = flow?.origin ? getObjectId(flow.origin) : flow?.id;
  const bindingFlowId = flow ? resolveLiveBindingFlowId(flow, eventSlotIdParam ?? undefined) : undefined;
  const performMode: FlowControlPerformMode = flow?.liveContext?.isRunning ? 'play' : 'simulation';
  const narrowViewMode = useFlowControlNarrowViewStore((s) => s.mode);
  const setNarrowViewMode = useFlowControlNarrowViewStore((s) => s.setMode);
  useHydrateFlowControlSimulationCueSheet(flow ?? undefined, planFlowId, performMode);
  useFlowControlLiveToSimulationReset(flow ?? undefined, planFlowId, performMode);
  const { resetToPlayingSegment } = useFlowControlSegmentBrowse();

  const handleNarrowViewModeChange = useCallback(
    (mode: FlowControlNarrowViewMode) => {
      setNarrowViewMode(mode);
      if (mode === 'list') {
        return;
      }
      resetToPlayingSegment(onSlotSelect);
      onRequestPerformOverlay?.(mode);
    },
    [onRequestPerformOverlay, onSlotSelect, resetToPlayingSegment, setNarrowViewMode],
  );

  useFlowControlAutoFollowPlayingSegment({
    enabled: Boolean(flow),
    isWideScreen,
    slots: flowPlan?.slots,
    onSlotSelect,
  });
  const entityRef = useMemo(() => {
    if (!bindingFlowId) return undefined;
    return performMode === 'play' ? getEntityRefFromFlow(bindingFlowId, flow?.liveContext) : `Flow:${planFlowId}`;
  }, [bindingFlowId, flow?.liveContext, performMode, planFlowId]);

  const socketEntity = useMemo(() => {
    if (!flow) {
      return { entityType: 'Flow' as const, entityId: planFlowId ?? '' };
    }
    return resolveSocketEntityFromFlow(flow, eventSlotIdParam ?? undefined);
  }, [flow, planFlowId, eventSlotIdParam]);

  const { data: liveContextState, dataUpdatedAt: liveContextQueryUpdatedAt } = useGetLiveContextStateQuery(entityRef, {
    enabled: !!entityRef && performMode === 'play',
  });
  const cueState = useLiveContextCueState(entityRef, undefined, liveContextState?.cueState);
  useNextCueRecalculating(performMode === 'play' ? entityRef : undefined, cueState);

  const liveContextStateRef = useRef(liveContextState);

  useEffect(() => {
    liveContextStateRef.current = liveContextState;
  }, [liveContextState]);

  usePlayerSocket(
    socketEntity.entityId,
    socketEntity.entityType,
    {
      onPlayersInvalidated: (entityId, entityType) => {
        queryClient.invalidateQueries({ queryKey: [GET_PLAYERS_QUERY_KEY, entityId, entityType] });
      },
      onLiveContextInvalidated: (_entityId, _entityType, meta) => {
        if (
          !shouldRefetchQueriesOnLiveContextInvalidated({
            liveIsRunning: performMode === 'play',
            isRunningFromEvent: meta?.isRunning,
          })
        ) {
          return;
        }
        if (entityRef) {
          queryClient.invalidateQueries({ queryKey: [GET_LIVE_CONTEXT_STATE_QUERY_KEY, entityRef] });
          void queryClient.refetchQueries({ queryKey: [GET_LIVE_CONTEXT_STATE_QUERY_KEY, entityRef] });
        }
        if (planFlowId) {
          void queryClient.refetchQueries({ queryKey: [GET_FLOW_QUERY_KEY, planFlowId] });
        }
      },
      onLiveLanguageStatus: () => {
        if (entityRef) {
          void queryClient.invalidateQueries({ queryKey: [GET_LIVE_LANGUAGE_STATUS_QUERY_KEY, entityRef] });
        }
      },
      onSequenceIndexChange: (data) => {
        applyServerSequenceIndexChange(data, {
          entityRef,
          queryClient,
          fallbackItemIds: resolveLiveSequenceItemIds(liveContextStateRef.current),
        });
      },
    },
    !!socketEntity.entityId,
  );

  if (!flow) return null;

  const layout = isWideScreen ? (
    <Box sx={{ display: 'flex', gap: 1, flex: 1, minHeight: 0, width: '100%', overflow: 'hidden' }}>
      <FlowControlSlotListColumn
        setShowFlowDetails={setShowFlowDetails}
        setFlowDetailsDirection={setFlowDetailsDirection}
        onSlotSelect={onSlotSelect}
        onShowTimingAlert={onShowTimingAlert}
        onShowPublishAlert={onShowPublishAlert}
      />
      <Box sx={{ ...sharedColumnSx, gap: 1, overflow: 'hidden' }}>
        {entityRef ? (
          <FlowControlNowNextStack
            flow={flow}
            performMode={performMode}
            liveContextState={liveContextState}
            planFlowId={planFlowId}
            entityRef={entityRef}
            liveContextQueryUpdatedAt={liveContextQueryUpdatedAt}
          />
        ) : null}
        {entityRef && (
          <PerformLowerControls
            flow={flow}
            entityRef={entityRef}
            eventSlotId={eventSlotIdParam ?? undefined}
            performMode={performMode}
            liveContextState={liveContextState}
            liveContextQueryUpdatedAt={liveContextQueryUpdatedAt}
            stickyFooter={false}
            flushEdges
          />
        )}
      </Box>
    </Box>
  ) : (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        width: '100%',
        overflow: 'hidden',
        bgcolor: 'background.default',
      }}
    >
      <Box
        sx={{
          width: '100%',
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
          height: '100%',
        }}
      >
        <FlowControlSlotListColumn
          setShowFlowDetails={setShowFlowDetails}
          setFlowDetailsDirection={setFlowDetailsDirection}
          onSlotSelect={onSlotSelect}
          onShowTimingAlert={onShowTimingAlert}
          onShowPublishAlert={onShowPublishAlert}
        />
      </Box>
      {entityRef && (
        <Box
          sx={{
            flexShrink: 0,
            borderTop: 1,
            borderColor: 'divider',
            bgcolor: 'background.paper',
            // Cancel parent Container `px` so the strip matches the full-bleed overlay footer.
            mx: { xs: -1, sm: -2 },
          }}
        >
          <PerformLowerControls
            flow={flow}
            entityRef={entityRef}
            eventSlotId={eventSlotIdParam ?? undefined}
            performMode={performMode}
            liveContextState={liveContextState}
            liveContextQueryUpdatedAt={liveContextQueryUpdatedAt}
            stickyFooter={false}
            flushEdges
            showNarrowViewModes
            narrowViewMode={narrowViewMode}
            onNarrowViewModeChange={handleNarrowViewModeChange}
          />
        </Box>
      )}
    </Box>
  );

  return (
    <FlowControlLiveStateProvider entityRef={entityRef} performMode={performMode}>
      {layout}
    </FlowControlLiveStateProvider>
  );
}
